import logger from './logger'
import supabase from './supabaseClient'

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''

class ApiError extends Error {
  constructor(message, status, code) {
    super(message)
    this.status = status
    this.code = code
    this.name = 'ApiError'
  }
}

// Device identifier for vote limiting
const getDeviceId = () => {
  const key = 'poll_device_id'
  let deviceId = localStorage.getItem(key)

  if (!deviceId) {
    // Generate a simple device ID based on timestamp and random string
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem(key, deviceId)
    logger.info('Generated new device ID', { deviceId })
  }

  return deviceId
}

// Track voted choices for this device (stores vote types: 'upvote' | 'downvote')
const getVotedChoices = () => {
  const key = 'poll_voted_choices'
  const voted = localStorage.getItem(key)
  if (!voted) return {}

  try {
    const parsed = JSON.parse(voted)
    // Handle migration from array format to object format
    if (Array.isArray(parsed)) {
      const obj = {}
      parsed.forEach(choiceId => {
        obj[choiceId] = 'upvote' // Default existing votes to upvote
      })
      localStorage.setItem(key, JSON.stringify(obj))
      return obj
    }
    return parsed
  } catch (e) {
    logger.warn('Failed to parse voted choices from localStorage', { error: e.message })
    return {}
  }
}

const addVotedChoice = (choiceId, voteType = 'upvote') => {
  const key = 'poll_voted_choices'
  const voted = getVotedChoices()
  voted[choiceId] = voteType
  localStorage.setItem(key, JSON.stringify(voted))
  logger.info('Added choice to voted list', { choiceId, voteType })
}

const hasVotedForChoice = (choiceId) => {
  const voted = getVotedChoices()
  return choiceId in voted
}

const getVoteType = (choiceId) => {
  const voted = getVotedChoices()
  return voted[choiceId] || null // Returns 'upvote', 'downvote', or null
}

const removeVotedChoice = (choiceId) => {
  const key = 'poll_voted_choices'
  const voted = getVotedChoices()
  delete voted[choiceId]
  localStorage.setItem(key, JSON.stringify(voted))
  logger.info('Removed choice from voted list', { choiceId })
}

/**
 * Get the current Supabase access token for authenticated API requests
 */
const getSupabaseToken = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token || null
  } catch (error) {
    logger.warn('Failed to get Supabase token', { error: error.message })
    return null
  }
}

const api = {
  // Note: Admin authentication is now handled via Supabase Auth with role-based access
  // The access token is automatically retrieved from Supabase session

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`
    const correlationId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9)

    // Detect if body is FormData to handle multipart uploads
    const isFormData = options.body instanceof FormData

    const defaultHeaders = {
      'x-correlation-id': correlationId
    }

    // Only set Content-Type for JSON requests; let browser set it for FormData
    if (!isFormData) {
      defaultHeaders['Content-Type'] = 'application/json'
    }

    // Add Authorization header if user is authenticated
    const token = await getSupabaseToken()
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`
    }

    const config = {
      ...options,
      credentials: 'include', // Include cookies for Supabase auth
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    }

    // Log body appropriately based on type
    let logBody
    if (config.body) {
      if (isFormData) {
        // For FormData, log field names instead of trying to parse
        const fieldNames = []
        for (const [key, value] of config.body.entries()) {
          fieldNames.push(key + (value instanceof File ? `:${value.name}` : ''))
        }
        logBody = `FormData(${fieldNames.join(', ')})`
      } else {
        logBody = JSON.parse(config.body)
      }
    }

    logger.debug(`API Request: ${options.method || 'GET'} ${endpoint}`, {
      correlationId,
      body: logBody
    })

    try {
      const response = await fetch(url, config)
      const data = await response.json().catch(() => null)

      if (!response.ok) {
        const errorMessage = data?.message || `HTTP ${response.status}: ${response.statusText}`
        const errorCode = data?.code || 'UNKNOWN_ERROR'

        logger.warn(`API Error: ${options.method || 'GET'} ${endpoint}`, {
          correlationId,
          status: response.status,
          code: errorCode,
          message: errorMessage
        })

        throw new ApiError(errorMessage, response.status, errorCode)
      }

      logger.debug(`API Success: ${options.method || 'GET'} ${endpoint}`, {
        correlationId,
        status: response.status
      })

      return data
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      // Network or other error
      logger.error(`API Network Error: ${options.method || 'GET'} ${endpoint}`, {
        correlationId,
        error: error.message
      })

      throw new ApiError('Network error - please check your connection', 0, 'NETWORK_ERROR')
    }
  },

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' })
  },

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  },

  async postForm(endpoint, formData, headers = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers
    })
  },

  async delete(endpoint, data) {
    return this.request(endpoint, {
      method: 'DELETE',
      body: JSON.stringify(data)
    })
  },

  async cleanupDatabase() {
    return this.request('/api/admin/cleanup', { method: 'DELETE' })
  },

  /**
   * Fetch authenticated user's polls (for dashboard)
   * @param {number} page - Page number (default 1)
   * @param {number} limit - Items per page (default 10)
   * @param {string} status - Status filter: 'visible' (default), 'all', 'active', 'expired', 'archived'
   */
  async getUserPolls(page = 1, limit = 10, status = 'visible') {
    return this.get(`/api/user/polls?page=${page}&limit=${limit}&status=${status}`)
  },

  /**
   * Fetch authenticated user's polls (for dashboard)
   * @param {number} page - Page number (default 1)
   * @param {number} limit - Items per page (default 10)
   */
  async getGuestPolls(page = 1, limit = 10) {
    const deviceId = getDeviceId()
    return this.get(`/api/guest/polls?deviceId=${deviceId}&page=${page}&limit=${limit}`)
  },

  /**
   * Update poll title (for inline editing)
   * @param {string} pollId - Poll ID
   * @param {string} title - New title
   */
  async updatePollTitle(pollId, title) {
    const deviceId = getDeviceId()
    return this.patch(`/api/polls/${pollId}`, { title, device_id: deviceId })
  },

  /**
   * Fetch authenticated user's recent activity
   */
  async getUserActivity() {
    return this.get('/api/user/activity')
  }
}

export { api, ApiError, getDeviceId, getVotedChoices, addVotedChoice, hasVotedForChoice, removeVotedChoice, getVoteType }
export default api
